if (!customElements.get('collection-carousel-tabs')) {
  class CollectionCarouselTabs extends HTMLElement {
    constructor() {
      super();
      this.productSwiperEls = [];
      this.productSwipers = [];
      this._tabClickHandlers = [];
    }

    connectedCallback() {
      this.bootstrap();
    }

    disconnectedCallback() {
      this.destroy();
    }

    bootstrap() {
      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', () => this.init(), { once: true });
        return;
      }
      this.init();
    }

    init() {
      this.destroy();

      const tabButtons = [...this.querySelectorAll('[data-tab-button]')];
      const productSwiperEls = [...this.querySelectorAll('[data-product-swiper]')];

      if (!productSwiperEls.length) return;

      this.productSwiperEls = productSwiperEls;
      this.productSwipers = new Array(productSwiperEls.length).fill(null);

      tabButtons.forEach((button) => {
        const handler = (event) => {
          event.preventDefault();
          const index = Number(button.dataset.tabIndex);
          if (Number.isNaN(index)) return;
          this.onTabChange(index);
        };

        button.addEventListener('click', handler);
        this._tabClickHandlers.push({ button, handler });
      });

      this.onTabChange(0);
    }

    ensureProductSwiper(index) {
      const targetIndex = Number(index);
      if (Number.isNaN(targetIndex) || targetIndex < 0 || targetIndex >= this.productSwiperEls.length) return null;

      if (this.productSwipers[targetIndex]) return this.productSwipers[targetIndex];

      const swiper = this.createProductSwiper(this.productSwiperEls[targetIndex]);
      this.productSwipers[targetIndex] = swiper;
      return swiper;
    }

    createProductSwiper(productEl) {
      const slides = productEl.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');
      if (!slides.length) return null;

      const slidesPerGroup = Number(this.dataset.productsPerGroup) || 3;
      const carouselRoot = productEl.closest('.product_list_container');
      const prevEl = carouselRoot?.querySelector('.swiper_button_prev');
      const nextEl = carouselRoot?.querySelector('.swiper_button_next');
      const paginationEl = carouselRoot?.querySelector('.swiper-pagination');

      return new Swiper(productEl, {
        nested: true,
        slidesPerView: 4,
        slidesPerGroup: slidesPerGroup,
        spaceBetween: 32,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        resizeObserver: true,
        watchOverflow: true,
        preventClicks: true,
        slideToClickedSlide: false,
        threshold: 30,
        navigation: {
          prevEl: prevEl,
          nextEl: nextEl,
        },
        pagination: {
          el: paginationEl,
          clickable: true,
        },
        on: {
          click: (swiper, event) => {
            if (event.target.tagName === 'A') {
              swiper.disable();
              window.location.href = event.target.href;
            }
          },
        },
        breakpoints: {
          1600: {
            slidesPerView: 4,
            slidesPerGroup: slidesPerGroup,
            spaceBetween: 32,
          },
          1300: {
            slidesPerView: 3.5,
            slidesPerGroup: Math.max(1, slidesPerGroup - 1),
            spaceBetween: 32,
          },
          900: {
            slidesPerView: 3,
            slidesPerGroup: Math.max(1, slidesPerGroup - 1),
            spaceBetween: 24,
          },
          750: {
            slidesPerView: 2.3,
            slidesPerGroup: 2,
            spaceBetween: 16,
          },
          320: {
            slidesPerView: 1.5,
            slidesPerGroup: 2,
            spaceBetween: 16,
          },
        },
      });
    }

    onTabChange(index) {
      const safeIndex = Math.min(Math.max(index, 0), this.productSwipers.length - 1);

      this.setActiveTab(safeIndex);

      this.querySelectorAll('[data-tab-panel]').forEach((panel, panelIndex) => {
        const isActive = panelIndex === safeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.style.display = isActive ? '' : 'none';
      });

      const activeSwiper = this.ensureProductSwiper(safeIndex);
      if (activeSwiper) {
        window.requestAnimationFrame(() => {
          activeSwiper.update();
        });
      }
    }

    setActiveTab(index) {
      this.querySelectorAll('[data-tab-button]').forEach((button, i) => {
        const isActive = i === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    destroy() {
      this._tabClickHandlers.forEach(({ button, handler }) => {
        button.removeEventListener('click', handler);
      });
      this._tabClickHandlers = [];

      this.productSwipers.forEach((swiper) => {
        if (swiper && !swiper.destroyed) swiper.destroy(true, true);
      });
      this.productSwiperEls = [];
      this.productSwipers = [];
    }
  }

  customElements.define('collection-carousel-tabs', CollectionCarouselTabs);
}

document.addEventListener('shopify:section:load', (event) => {
  const carousel = event.target.querySelector('collection-carousel-tabs');
  if (carousel && typeof carousel.init === 'function') carousel.init();
});

document.addEventListener('shopify:section:unload', (event) => {
  const carousel = event.target.querySelector('collection-carousel-tabs');
  if (carousel && typeof carousel.destroy === 'function') carousel.destroy();
});
